import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { View, Platform, useWindowDimensions, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import apis from '@api/api';
import { useSelector, useDispatch } from 'react-redux';
import * as Animatable from 'react-native-animatable';
import DefaultScreen from '@components/Global/DefaultScreenView';
import AddUserModal, { AddUserModalHandle } from './Components/AddUserModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import UserOptionsModal, { UserOptionsModalHandle } from './Components/UserOptionsModal';
import EditUserModal, { EditUserModalHandle } from './Components/EditUserModal';
import { HIDE_TOOLBAR, ADD_PENDING_USER, REMOVE_PENDING_USER, UPDATE_USER_STATUS } from '@reduxLocal/actions/types';
import { AntDesign, FontAwesome5 } from '@expo/vector-icons';
import { DefaultRootState } from '@reduxLocal/persistState';
import UserHierarchy, { UserHierarchyHandle } from './Components/userHierarchy';
import OptimizedUserHierarchy, { OptimizedUserHierarchyHandle } from './Components/OptimizedUserHierarchy';
import DepartmentHierarchy, { DepartmentHierarchyHandle } from './Components/DepartmentHierarchy';
import PendingUsersPanel from './Components/PendingUsersPanel';
import { StyledModalHandle, Title, Text as ThemedText } from '@constants/Themed';
import { Text } from 'react-native';
import AddDepartmentModal from './Components/AddDepartmentModal';
import UserList from './Components/userList';
import UserStats from './Components/UserStats';
import normalizeValues from './utils/normalizeValues';
import AddToDepartmentModal from './Components/AddToDepartmentModal';
import { useDeviceType } from '@hooks/useDeviceType';
import BottomSheet from '@gorhom/bottom-sheet';
import globalStyles from "@stylesheet";
import DragDropWrapper from '@components/Global/DragDropWrapper';
import useSubscriptionStatus from '@hooks/useSubscriptionStatus';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import AgentBuilderModal, { AgentBuilderModalHandle } from './Components/AgentBuilderModal';


const UsersScreen = ({ navigation }) => {

  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const theme = themeSelector();

  // Debug: Check if currentCompany is available
  useEffect(() => {
    console.log('🏢 UsersScreen - currentCompany status:', {
      hasCurrentCompany: !!currentCompany,
      companyId: currentCompany?._id,
      companyName: currentCompany?.name,
      fullCompany: currentCompany
    });
  }, [currentCompany]);
  const zoomableViewRef = useRef<UserHierarchyHandle>(null);
  const optimizedHierarchyRef = useRef<OptimizedUserHierarchyHandle>(null);
  const departmentHierarchyRef = useRef<DepartmentHierarchyHandle>(null);
  const addDepartmentRef = useRef<StyledModalHandle>(null);
  const addUserRef = useRef<AddUserModalHandle>(null);
  const addAgentRef = useRef<AgentBuilderModalHandle>(null);
  const userOptionsRef = useRef<UserOptionsModalHandle>(null);
  const editUserModalRef = useRef<EditUserModalHandle>(null);
  const dispatch = useDispatch();
  const [listVisible, setListVisible] = useState(false);
  const alertDialog = useAlertDialog();
  const [userList, setUserList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // const departmentItems = [...new Set(userList?.map(element => element.department))];
  const userSettingsRef = useRef(null);
  const [helpState, setHelpState] = useState(true);
  // const departments = departmentItems;
  // companyId comes from API response (backend uses session companyNumber)
  const [companyId, setCompanyId] = useState("");
  const [owner, setOwner] = useState<any>(undefined);
  const [links, setLinks] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const queryClient = useQueryClient();
  const addToDepartmentRef = useRef<StyledModalHandle>(null);
  const [showLists, setShowLists] = useState(true);
  const { height, width } = useWindowDimensions();


  const snapPoints = ['10%', '35%'];
  const { isTablet } = useDeviceType();
  const bottomRef = useRef<BottomSheet>(null);
  const bottomPosition = useRef(0);
  const subscription = useSubscriptionStatus();
  const [useOptimizedHierarchy, setUseOptimizedHierarchy] = useState(false); // Toggle for testing
  const [useDepartmentView, setUseDepartmentView] = useState(false); // Toggle for department view

  const _handleResetUsers = async () => {
    setHierarchyLoading(true);
    const normalVals = await normalizeValues(userList, links, owner, departmentList, width, height);
    //Normalize the values of the user list
    setUserList(normalVals.users);
    //The department list
    setDepartmentList(normalVals.departments || []);
    setHierarchyLoading(false);
  }


  const _handleReset = useCallback(() => {
    setLoaded(false);
    setTimeout(() => {
      setLoaded(true);
    }, 1000);
  }, [loaded, userList])

  function _handleSnap(target, source) {
    if (target && target !== "" && target !== null && target !== undefined) {
      userOptionsRef.current!.openModal(target, source);
    }
  }

  // Handle optimistic link updates
  const _handleOptimisticLink = useCallback((link, shouldRemove = false) => {
    console.log('🎯 Handling optimistic link:', { link, shouldRemove });

    if (shouldRemove) {
      // Remove the link from local state
      setLinks(prevLinks => prevLinks.filter(l => l.id !== link.id));
      console.log('🗑️ Removed optimistic link:', link.id);
    } else {
      // Add the link to local state
      setLinks(prevLinks => {
        // Check if link already exists to avoid duplicates
        const exists = prevLinks.some(l => l.id === link.id);
        if (exists) {
          console.log('⚠️ Link already exists, skipping:', link.id);
          return prevLinks;
        }

        const newLinks = [...prevLinks, link];
        console.log('➕ Added optimistic link:', link.id, 'Total links:', newLinks.length);
        return newLinks;
      });
    }
  }, []);


  // Invalidate cache on mount to ensure fresh data
  useEffect(() => {
    console.log('🔄 UsersScreen mounted - invalidating cache and forcing fresh fetch');
    queryClient.invalidateQueries({ queryKey: ['userHierarchy'] });
  }, []);

  const { status, data, error, isFetching, isLoading, refetch } = useQuery({
    // Use stable queryKey - backend uses session to determine hierarchy type
    // Backend returns company hierarchy if companyNumber in session, otherwise user hierarchy
    // We don't need to include companyId in key since backend handles it via session
    queryKey: ['userHierarchy'],
    queryFn: async () => {
      console.log('📡 Query starting - backend uses session to determine hierarchy type');
      console.log('📡 Backend will return company hierarchy if companyNumber in session, otherwise user hierarchy');
      console.log('📡 Current company from Redux (for reference only):', currentCompany);

      // Backend uses session data (companyNumber for company hierarchy, userId for user hierarchy)
      // No need to pass companyId - backend reads from session
      console.log('🚀 Making API call to getUserHierarchy...');
      let res;
      try {
        res = await apis.getUserHierarchy();
        console.log('✅ API call successful');
        console.log('📊 Query response received:', {
          status: res?.status,
          dataLength: res?.data?.length,
          owner: !!res?.owner,
          departments: res?.departments?.length,
          companyId: res?.companyId,
          hasData: !!res?.data,
          isUserHierarchy: !res?.companyId, // User hierarchy has no companyId
          hierarchyType: res?.companyId ? 'company' : 'user',
          dataSample: res?.data?.slice(0, 2),
          ownerData: res?.owner ? {
            _id: res.owner._id,
            email: res.owner.email,
            department: res.owner.profile_information?.department
          } : null,
          fullResponse: res
        });
      } catch (apiError) {
        console.error('❌ API call failed:', apiError);
        throw apiError;
      }

      // Check if we have valid data instead of status code
      if (!res || (!res.data && !res.owner)) {
        console.log('❌ Query failed - no data received:', { hasData: !!res?.data, hasOwner: !!res?.owner, res });
        throw new Error('Failed to fetch hierarchy: No data received');
      }
      
      console.log('✅ API response validation passed');

      try {
        // Extract links from hierarchyView if available
        const links = res.hierarchyView?.links || res.links || [];

        // Filter out hierarchy entries with null users and extract actual user data
        const validUsers = (res.data || []).filter(entry => entry.user !== null);

        console.log('🔍 Processing hierarchy data:', {
          totalEntries: res.data?.length,
          validUsers: validUsers.length,
          links: links.length,
          hasOwner: !!res.owner,
          departments: res.departments?.length,
          isUserView: !res.companyId, // User view has no companyId
          hierarchyType: res.companyId ? 'company' : 'user',
          validUsersSample: validUsers.slice(0, 2).map(u => ({
            _id: u._id,
            hasUser: !!u.user,
            userData: u.user ? {
              _id: u.user._id,
              email: u.user.email,
              department: u.user.profile_information?.department
            } : null
          })),
          allValidUsers: validUsers.map(u => ({
            _id: u._id,
            userId: u.user?._id,
            userEmail: u.user?.email,
            userName: u.user?.profile_information?.name,
            userDepartment: u.user?.profile_information?.department,
            hasUser: !!u.user
          }))
        });

        // Set links immediately
        setLinks(links);

        const normalVals = await normalizeValues(validUsers, links, res.owner, res.departments, width, height);
        console.log('🔧 Normalized values:', {
          usersLength: normalVals?.users?.length,
          departmentsLength: normalVals?.departments?.length,
          hasOwner: !!normalVals,
          normalizedUsers: normalVals?.users?.map(u => ({
            _id: u._id || u.user?._id,
            userId: u.user?._id,
            userEmail: u.user?.email || u.email,
            userName: u.user?.profile_information?.name || u.profile_information?.name,
            userDepartment: u.user?.profile_information?.department || u.profile_information?.department || u.department,
            type: u.type
          }))
        });

        //Normalize the values of the user list
        setUserList(normalVals.users);
        zoomableViewRef.current?.moveTo(900, 500);
        //The department list
        setDepartmentList(normalVals.departments || []);
        //Set the companyId from the response
        if (res.companyId) {
          setCompanyId(res.companyId);
          console.log('✅ UsersScreen - companyId set from API response:', res.companyId);
        } else {
          // Clear companyId for user hierarchy
          setCompanyId("");
          console.log('✅ UsersScreen - user hierarchy (no companyId)');
        }
        //The owner of the hierarchy
        console.log('🔍 UsersScreen setting owner from API response:', {
          owner: res.owner,
          ownerId: res.owner?._id,
          ownerEmail: res.owner?.email,
          ownerProfile: res.owner?.profile_information,
          hasOwner: !!res.owner
        });
        setOwner(res.owner);
        setHierarchyLoading(false);
        setLoaded(true);
        return normalVals.users;
      } catch (e) {
        console.log('❌ Normalization failed:', e);
        
        // Extract valid users even in error case
        const validUsers = (res.data || []).filter(entry => entry?.user !== null);
        const links = res.hierarchyView?.links || res.links || [];
        
        setUserList(validUsers);
        setLinks(links);
        setDepartmentList(res.departments || []);
        //Set the companyId from the response
        if (res.companyId) {
          setCompanyId(res.companyId);
          console.log('✅ UsersScreen - companyId set from API response (catch):', res.companyId);
        } else {
          setCompanyId("");
        }
        console.log('🔍 UsersScreen setting owner from API response (catch block):', {
          owner: res.owner,
          ownerId: res.owner?._id,
          ownerEmail: res.owner?.email,
          ownerProfile: res.owner?.profile_information,
          hasOwner: !!res.owner
        });
        setOwner(res.owner);
        setLoaded(true);
        setHierarchyLoading(false);
        return [];
      }
    },
    refetchOnWindowFocus: true,
    refetchOnMount: 'always', // Always refetch on mount, even if data exists in cache
    // Query always enabled - backend uses session to determine hierarchy type
    retry: 1, // Only retry once on failure
    enabled: true,
    staleTime: 0, // Always consider data stale to force fresh fetches on mount
    gcTime: 5 * 60 * 1000 // Keep in cache for 5 minutes (formerly cacheTime)
  });

  // Refetch when currentCompany changes (company switch) or when cleared (user hierarchy)
  // This ensures we get fresh data when the session context changes
  // Backend session is updated separately, so we refetch to get the new hierarchy
  const previousCompanyIdRef = useRef<string | null | undefined>(undefined);
  
  useEffect(() => {
    const currentCompanyId = currentCompany?._id || currentCompany?.id || null;
    const companyChanged = currentCompanyId !== previousCompanyIdRef.current;
    
    // Refetch if company context changed:
    // 1. Company ID changed (switched between companies)
    // 2. Company was cleared (switched to user hierarchy)
    // 3. Company was set (switched from user to company hierarchy)
    if (companyChanged && previousCompanyIdRef.current !== undefined) {
      // Only refetch if this is not the initial mount (previousCompanyIdRef.current !== undefined)
      console.log('🔄 Company context changed, refetching hierarchy:', {
        previousCompanyId: previousCompanyIdRef.current,
        newCompanyId: currentCompanyId || 'user hierarchy',
        isInitialMount: previousCompanyIdRef.current === undefined
      });
      
      previousCompanyIdRef.current = currentCompanyId;
      
      // Small delay to ensure backend session is updated
      const timeoutId = setTimeout(() => {
        refetch();
      }, 200);
      return () => clearTimeout(timeoutId);
    } else {
      // Update ref for tracking (including initial mount)
      previousCompanyIdRef.current = currentCompanyId;
    }
  }, [currentCompany?._id, currentCompany?.id, refetch]);

  // Debug: Log query state
  useEffect(() => {
    console.log('📊 useQuery state:', {
      status,
      isFetching,
      isLoading,
      hasError: !!error,
      hasData: !!data,
      dataLength: data?.length,
      userListLength: userList?.length,
      hasOwner: !!owner,
      errorMessage: error?.message
    });

    if (error) {
      console.error('❌ useQuery error:', error);
    }

    if (data && !isLoading && !isFetching) {
      console.log('✅ useQuery completed successfully:', {
        dataLength: data?.length,
        userListLength: userList?.length,
        hasOwner: !!owner
      });
    }
  }, [status, isFetching, isLoading, error, data, userList, owner]);

  useEffect(
    useCallback(() => {
      const beforeRemoveListener = () => {
        // implement function to save changes
        dispatch({ type: HIDE_TOOLBAR });
      };

      const unsubscribe = navigation.addListener('beforeRemove', beforeRemoveListener);

      return () => {
        //Dismount the user hierarchy component
        unsubscribe();
      }
      // Clean up the listener when the component loses focus
    }, [navigation])
  );

  const _clickCallback = (user: { x: number, y: number }) => {
    if (useDepartmentView) {
      departmentHierarchyRef.current?.zoomTo(0.8, { x: user.x, y: user.y });
    } else if (useOptimizedHierarchy) {
      optimizedHierarchyRef.current?.zoomTo(0.8, { x: user.x, y: user.y });
    } else {
      zoomableViewRef.current?.zoomTo(0.8, { x: user.x, y: user.y });
    }
  }

  const _openAddUserModal = useCallback(() => {
    addUserRef.current?.openModal();
  }, [addUserRef]);

  const _openAddToDepartmentModal = useCallback((department) => {

    addToDepartmentRef.current?.openModal(department);

  }, []);

  // Drag-drop invite functionality
  const handleUserDrop = useCallback((userData: any) => {
    console.log('🎯 User dropped for invitation:', userData);
    console.log('🎯 handleUserDrop called - this should not happen if onConfirmDrop is provided');
    // This will trigger the confirmation dialog
  }, []);

  const handleConfirmUserInvite = useCallback(async (userData: any) => {
    console.log('🎯 Confirming user invitation:', userData);

    try {
      // Validate required user data
      if (!userData._id && !userData.email) {
        throw new Error('Invalid user data: missing _id or email');
      }

      // Check user subscription from Redux state
      if (!subscription) {
        throw new Error('User must be subscribed to invite users');
      }

      // First, add user to hierarchy in pending state
      const pendingUser = {
        _id: userData._id || `pending-${Date.now()}`,
        email: userData.email || userData.profile_information?.email,
        profile_information: userData.profile_information || {
          name: userData.name || 'Unknown User',
          surname: userData.surname || '',
          avatar: userData.avatar || { img: null }
        },
        status: 'pending',
        role: userData.role || 'member',
        isPending: true,
        invitedAt: new Date().toISOString()
      };

      console.log('🟡 Created pending user:', {
        _id: pendingUser._id,
        isPending: pendingUser.isPending,
        status: pendingUser.status,
        name: pendingUser.profile_information.name
      });

      // Add pending user to Redux state immediately for UI feedback
      console.log('🎯 Dispatching ADD_PENDING_USER:', pendingUser);
      console.log('🎯 User ID comparison:', {
        pendingUserId: pendingUser._id,
        userDataId: userData._id,
        userDataIdAlt: userData.id,
        userEmail: userData.email
      });

      dispatch({
        type: ADD_PENDING_USER,
        payload: pendingUser
      });

      // Debug: Check Redux state after dispatch
      setTimeout(() => {
        console.log('🔍 Redux state after ADD_PENDING_USER dispatch');
      }, 100);

      // Prepare user data with default role for API - ensure required fields
      const userWithRole = {
        _id: userData._id || `pending-${Date.now()}`,
        email: userData.email || userData.profile_information?.email,
        profile_information: userData.profile_information || {
          name: userData.name || 'Unknown User',
          surname: userData.surname || '',
          avatar: userData.avatar || { img: null }
        },
        role: userData.role || 'member' // Default role for invited users
      };

      console.log('📤 Sending API request with user data:', userWithRole);

      // Call the API to add user to company
      console.log('📤 Calling API with user data:', userWithRole);
      const result = await apis.addUserToCompany({ users: [userWithRole] });
      console.log('✅ API Response received:', result);

      // Check if the API call was successful
      if (!result) {
        console.error('❌ No response received from API');
        throw new Error('API call failed: No response received');
      }

      console.log('🔍 API Response structure:', {
        result: result,
        success: result.success,
        message: result.message,
        summary: result.summary
      });

      // Handle different response structures
      if (result.success === false) {
        console.error('❌ API returned success: false', result);
        throw new Error(result.message || 'Failed to invite user');
      }

      // Check for warnings or already invited users
      if (result.warnings && result.warnings.length > 0) {
        console.warn('⚠️ Warnings during invitation:', result.warnings);
      }

      if (result.alreadyInvited && result.alreadyInvited.length > 0) {
        console.warn('⚠️ Some users were already invited:', result.alreadyInvited);
      }

      console.log('✅ API call completed successfully');

      // Keep user in pending state - don't update to active until invitation is accepted
      console.log('🎯 User invitation sent successfully, keeping in pending state');
      console.log('📝 User will remain pending until they accept the invitation');

      try {
        // Refresh the user hierarchy
        console.log('🔄 Invalidating userHierarchy query');
        queryClient.invalidateQueries({ queryKey: ['userHierarchy'] });
        console.log('✅ Query invalidation completed');
      } catch (queryError) {
        console.error('❌ Error in query invalidation:', queryError);
        // Don't throw here, just log the error
      }

      console.log('✅ handleConfirmUserInvite completed successfully');
      return result;
    } catch (error) {
      console.error('❌ Error inviting user:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });

      // Remove pending user on error
      console.log('🎯 Dispatching REMOVE_PENDING_USER:', userData._id || `pending-${Date.now()}`);
      dispatch({
        type: REMOVE_PENDING_USER,
        payload: userData._id || `pending-${Date.now()}`
      });

      // Create a more user-friendly error message
      let errorMessage = 'Failed to invite user';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Create a new error with the user-friendly message
      const userFriendlyError = new Error(errorMessage);
      (userFriendlyError as any).originalError = error;
      throw userFriendlyError;
    }
  }, [queryClient, dispatch]);

  // useEffect(
  //   useCallback(() => {
  //     dispatch({ type: USER_HIERARCHY_FOCUSED, payload: true });

  //     // Return a cleanup function
  //     return () => {
  //       // Your cleanup logic here
  //       dispatch({ type: USER_HIERARCHY_FOCUSED, payload: false });

  //     };
  //   }, [hierarchyFocused])
  // );

  const changeShowStats = useCallback((v: boolean) => {
    setShowLists(!showLists);
  }, [showLists])

  // Define button actions from the old toolbar
  const buttonActions = [
    { id: 'addUser', icon: 'user-add', label: 'User', onPress: () => addUserRef.current?.openModal?.() },
    {
      id: 'addAgent', beta: true, icon: 'robot', label: 'Agent', onPress: () =>
        addAgentRef.current?.openModal()
    },
    { id: 'addDepartment', icon: 'folder-add', label: 'Dept', onPress: () => addDepartmentRef.current?.openModal?.() },
    {
      id: 'zoomIn', icon: 'plus-square', label: 'Zoom+', onPress: () => {
        if (useDepartmentView) {
          departmentHierarchyRef.current?.zoomBy(0.05);
        } else if (useOptimizedHierarchy) {
          optimizedHierarchyRef.current?.zoomBy(0.05);
        } else {
          zoomableViewRef.current?.zoomBy(0.05);
        }
      }
    },
    {
      id: 'zoomOut', icon: 'minus-square', label: 'Zoom-', onPress: () => {
        if (useDepartmentView) {
          departmentHierarchyRef.current?.zoomBy(-0.05);
        } else if (useOptimizedHierarchy) {
          optimizedHierarchyRef.current?.zoomBy(-0.05);
        } else {
          zoomableViewRef.current?.zoomBy(-0.05);
        }
      }
    },
    { id: 'resetUsers', icon: 'reload', label: 'Reset', onPress: () => _handleResetUsers() },
    {
      id: 'centerView', icon: 'plus-circle', label: 'Center', onPress: () => {
        if (useDepartmentView) {
          departmentHierarchyRef.current?.centerAllDepartments();
        } else if (useOptimizedHierarchy) {
          optimizedHierarchyRef.current?.centerAllComponents();
        } else {
          zoomableViewRef.current?.centerAllComponents();
        }
      }
    },
    { id: 'refresh', icon: 'reload', label: 'Refresh', onPress: () => refetch() },
  ];

  return (
    <DefaultScreen scrollDisabled noMargin containerStyle={{ paddingTop: 0, paddingBottom: currentCompany && 30, }} style={{ paddingTop: 0, flex: 1, paddingBottom: currentCompany && 30, top: -30, paddingHorizontal: 0, backgroundColor: "transparent" }}>

      {/* Horizontal Button Bar - Single Pill */}
      <View style={[styles.buttonBar, globalStyles.centerEverything, { top: isTablet ? 60 : 50 }]}>
        <View style={[
          styles.pillContainer,
          { 
            backgroundColor: theme ? 'white' : '#333333',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 5,
            elevation: 5,
          }
        ]}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.buttonScrollContainer}
          >
            {buttonActions.map((button, index) => (
              <TouchableOpacity
                key={button.id}
                style={[
                  styles.actionButton,
                  index !== buttonActions.length - 1 && {
                    borderRightWidth: 1,
                    borderRightColor: theme ? 'rgba(45, 55, 72, 0.15)' : 'rgba(255, 255, 255, 0.15)',
                  },
                  { padding: 4, gap: 5 }
                ]}
                onPress={button.onPress}
                activeOpacity={0.7}
              >
                <View style={{ position: 'relative', alignItems: 'center' }}>
                  <AntDesign
                    name={button.icon as any}
                    size={20}
                    style={{ top: 3 }}
                    color={theme ? '#2d3748' : '#ffffff'}
                  />
                  {button.beta && (
                    <View style={[
                      styles.betaBadge,
                      {
                        top: -4,
                        right: -27,
                        backgroundColor: theme ? '#FF6B6B' : '#FF8C8C',
                      }
                    ]}>
                      <Text style={styles.betaText}>BETA</Text>
                    </View>
                  )}
                </View>
                <Text style={[
                  styles.buttonText,
                  { color: theme ? '#2d3748' : '#ffffff' }
                ]}>
                  {button.label}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>

      <AddDepartmentModal ref={addDepartmentRef} />
      <UserOptionsModal ref={userOptionsRef} callBack={() => {
        queryClient.invalidateQueries({ queryKey: ['userHierarchy'] });
      }} userList={userList} />
      <EditUserModal 
        ref={editUserModalRef} 
        companyId={companyId}
        isUserView={!companyId} // User view when no companyId
        departmentList={departmentList} // Pass department list for company view
      />
      <AddUserModal departmentItems={[]} ref={addUserRef} company={companyId} />
      <AddToDepartmentModal ref={addToDepartmentRef} />
      <AgentBuilderModal 
        ref={addAgentRef} 
        userList={userList}
        onAgentCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['userHierarchy'] });
          refetch();
        }}
      />
      <View style={{ flex: 1, flexDirection: Platform.OS != 'web' || !isTablet ? "column-reverse" : "row" }}>
        <Animatable.View transition={'flex'} style={{ flex: listVisible ? 0.85 : 1 }} direction={listVisible ? 'normal' : 'reverse'}>
          <View style={{ flex: 4.5, alignSelf: 'stretch' }}>
            {useDepartmentView ? (
              <DepartmentHierarchy
                key="Department-Hierarchy"
                userList={userList.length > 0 ? userList : []}
                owner={owner}
                isLoading={isFetching || isLoading || hierarchyLoading}
                ref={departmentHierarchyRef}
                onDepartmentSnap={(targetDept, sourceDept) => {
                  console.log('🔗🔗🔗 PARENT CALLBACK TRIGGERED in UsersScreen:', {
                    source: sourceDept,
                    target: targetDept,
                    timestamp: Date.now()
                  });
                  // Show modal or create connection here
                  // For now, could show addToDepartmentRef modal or create a link
                  // addToDepartmentRef.current?.openModal();

                  // You can replace this with your modal logic
                  alert(`Connection: ${sourceDept} -> ${targetDept}`);
                }}
              />
            ) : useOptimizedHierarchy ? (
              <OptimizedUserHierarchy
                loadingError={error}
                  refresh={refetch}
                key="Optimized-User-Hierarchy"
                openAddToDepartmentModal={_openAddToDepartmentModal}
                departmentList={departmentList}
                  linkList={links}
                isLoading={(() => {
                  const finalIsLoading = isFetching || isLoading || hierarchyLoading;
                  console.log('🚦 Optimized UsersScreen Loading States:', {
                    isFetching,
                    isLoading,
                    hierarchyLoading,
                    finalIsLoading,
                    userListLength: userList?.length,
                    hasOwner: !!owner,
                    departmentListLength: departmentList?.length
                  });
                  return finalIsLoading;
                })()}
                  owner={owner}
                companyId={__DEV__ ? "65cb848f81bcc12ec3f0fced" : companyId}
                ref={optimizedHierarchyRef}
                  userList={userList}
                uniqueDepartmentList={undefined}
                openAddUserModal={_openAddUserModal}
                handleSnap={_handleSnap}
              />
            ) : (
                  <UserHierarchy
                    refresh={refetch}
                    loadingError={error}
                    key="Legacy-User-Hierarchy"
                    openAddToDepartmentModal={_openAddToDepartmentModal}
                    departmentList={departmentList}
                    linkList={links}
                    isLoading={(() => {
                      const finalIsLoading = isFetching || isLoading || hierarchyLoading;
                    
                    return finalIsLoading;
                    })()}
                    owner={owner}
                    primaryUser={owner}
                    companyId={__DEV__ ? "65cb848f81bcc12ec3f0fced" : companyId}
                    ref={zoomableViewRef}
                    userList={userList}
                    uniqueDepartmentList={undefined}
                    openAddUserModal={_openAddUserModal}
                    handleSnap={_handleSnap}
                    onUserPress={(user) => {
                      editUserModalRef.current?.openModal(user);
                    }}
                    // AcademyCourseEditor pattern: Calculate initial positions with department grouping
                    initialUsers={(() => {
                      const currentUser = owner;
                      const users = userList;

                      if (!currentUser || !users) return undefined;

                      // CRITICAL: Unwrap nested user structures before calculating positions
                      const unwrappedUsers = users.map(user => {
                        // If user has nested structure (from API/connectedUsers), unwrap it
                        if (user.user && user.user._id && typeof user.user === 'object') {
                          return {
                            ...user.user, // Extract the actual user object
                            department: user.department || user.user.profile_information?.department,
                            role: user.role,
                            status: user.status,
                            position: user.position, // Preserve position if it exists
                            // Preserve agent properties
                            type: user.type,
                            isAgent: user.isAgent,
                            agentType: user.agentType,
                            agentMode: user.agentMode
                          };
                        }
                        // Already unwrapped or direct user object (including agents)
                        return user;
                      }).filter(user =>
                        user && user._id && // Ensure valid user
                        // Allow agents through, or filter out pending users
                        ((user as any).isAgent || (user as any).type === 'agent') ||
                        (!(user as any).isPending && (user as any).status !== 'pending')
                      );

                      console.log('🎯 Calculating initialUsers positions:', {
                        ownerId: currentUser?._id,
                        userListLength: users.length,
                        unwrappedUsersLength: unwrappedUsers.length,
                        unwrappedSample: unwrappedUsers.slice(0, 2).map(u => ({
                          _id: u._id,
                          email: u.email,
                          name: u.profile_information?.name
                        }))
                      });

                      // Calculate positions like AcademyCourseEditor does
                      const componentWidth = 200;
                      const componentHeight = 120;
                      const primaryX = 2000 - (componentWidth / 2);
                      const primaryY = 2000 - (componentHeight / 2);

                      const usersPerLevel = 4;
                      const levelSpacing = componentWidth + 100;
                      const moduleSpacing = componentHeight + 50;

                      const allUsers = [currentUser, ...unwrappedUsers];

                      return allUsers.map((user, index) => {
                        if (index === 0) {
                          // Current user at center
                          return {
                            ...user,
                            x: primaryX,
                            y: primaryY
                          };
                        }

                        // Other users in levels to the right
                        const level = Math.floor((index - 1) / usersPerLevel);
                        const positionInLevel = (index - 1) % usersPerLevel;
                        const levelX = primaryX + levelSpacing + (level * levelSpacing);
                        const levelY = primaryY + (positionInLevel - (usersPerLevel - 1) / 2) * moduleSpacing;

                        return {
                          ...user,
                          x: levelX,
                          y: levelY
                        };
                      });
                    })()}
                  />
            )}
          </View>

        </Animatable.View>
      </View>

      {/* Pending Users Panel - Fixed position top right */}
      <PendingUsersPanel
        onRemovePendingUser={(userId) => {
          console.log('🗑️ Removing pending user:', userId);
          dispatch({
            type: REMOVE_PENDING_USER,
            payload: userId
          });
        }}
        onResendInvite={(user) => {
          console.log('📧 Resending invite to:', user);
          // TODO: Implement resend invite functionality
        }}
      />

      {/* DragDropWrapper as overlay */}
      {
        Platform.OS === 'web' &&
      <DragDropWrapper
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000
        }}
        onDrop={handleUserDrop}
        onConfirmDrop={handleConfirmUserInvite}
        confirmDialogTitle="Invite Team Member"
        confirmDialogMessage={(userData) => {
          const userName = userData?.profile_information?.name || userData?.name || 'this user';
          return `Invite ${userName} to join your team?`;
        }}
        dropZoneMessage={(userData) => {
          const userName = userData?.profile_information?.name || userData?.name || 'user';
          return `Drop ${userName} here to invite to team`;
        }}
        dropZoneIcon="person-add"
      >
        <View style={{ flex: 1 }} />
      </DragDropWrapper>
      }
      {/* {
        currentCompany &&
        <BottomSheet
          ref={bottomRef}
            index={0}
            snapPoints={snapPoints}
            enableDynamicSizing={false}
          style={{
            marginLeft: Platform.select({ web: 350 }),
            marginRight: Platform.select({ web: width * 0.1, default: 0 }),
            borderRadius: 20,
            shadowRadius: 20,
            shadowOpacity: .2,
            borderTopStartRadius: 20,
            borderTopEndRadius: 20
          }}
            handleComponent={() => (
            <View style={{ backgroundColor: "rgb(90,192,234)" }} />
          )}
          >
          <Animatable.View transition={['minHeight']} style={{ flex: 1, backgroundColor: theme ? "white" : "black", minHeight: showLists ? 80 : 40, flexDirection: "row" }}>
            {
              Platform.OS === 'web' &&
              <UserList showLists={showLists} setShowLists={changeShowStats} clickCallback={_clickCallback} departmentList={departmentList} flatten userList={data} departmentName={"All"} />
            }
            <UserStats
              addUser={_openAddUserModal}
              setShowLists={() => {
                if (bottomPosition.current === 1) {
                  bottomPosition.current = 0;
                  bottomRef.current?.snapToIndex(0);
                }
                if (bottomRef.current) {
                  bottomPosition.current = 1;
                  bottomRef.current?.snapToIndex(1);
                }
              }} showLists={showLists} companyId={companyId} userList={data} />
          </Animatable.View>
        </BottomSheet>
      } */}

      {/* User Options Modal for Link Creation */}
      <UserOptionsModal
        userList={userList}
        callBack={_handleOptimisticLink}
        ref={userOptionsRef}
      />
    </DefaultScreen >
  )
}

const styles = StyleSheet.create({
  buttonBar: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 50 : 30,
    left: 0,
    right: 0,
    height: 60,
    zIndex: 1000,
    paddingHorizontal: 10,
    backgroundColor: 'transparent',
  },
  pillContainer: {
    borderRadius: 30,
    paddingHorizontal: 8,
    paddingVertical: 4,
    maxWidth: '95%',
    alignSelf: 'center',
  },
  buttonScrollContainer: {
    alignItems: 'center',
    paddingHorizontal: 2,
    flexDirection: 'row',
  },
  actionButton: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 4,
    minWidth: 60,
  },
  buttonText: {
    fontSize: 10,
    fontWeight: '600',
    marginTop: 2,
    textAlign: 'center',
  },
  betaBadge: {
    position: 'absolute',
    top: -2,
    right: -8,
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 6,
    minWidth: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  betaText: {
    fontSize: 7,
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: 0.5,
  },
});

export default UsersScreen;
